<template>
    <div>
        <div v-if="loading" class="raster-loading py-5 text-center">
            <b-spinner variant="primary" />
            <div class="mt-3 font-weight-bold">Đang tải dữ liệu tại điểm...</div>
            <div class="text-muted" v-if="detailData.lat != null && detailData.lng != null">
                ({{ detailData.lat.toFixed(6) }}, {{ detailData.lng.toFixed(6) }})
            </div>
        </div>
        <b-alert v-else-if="errorMessage" show variant="warning" class="mb-0">
            {{ errorMessage }}
        </b-alert>
        <template v-else>
            <b-card class="detail-card mb-2 raster-info-card" header-class="raster-info-card-header"
                :style="cardThemeStyle" header-text-variant="white">
                <template #header>
                    <h4 class="font-weight-bold mb-1 text-center raster-name-text">
                        {{ nameText }}
                    </h4>
                    <h5 class="mb-0 text-center raster-address-text">
                        {{ addressWithCoordinates }}
                    </h5>
                </template>
                <b-row class="no-gutters align-items-center raster-body-row">
                    <b-col cols="5" class="text-center">
                        <h1 class="mb-0 font-weight-bold raster-last-value-text">
                            {{ lastValueText }}
                        </h1>
                    </b-col>
                    <b-col cols="2" class="text-center">
                        <b-img v-if="legendIconUrl" fluid class="raster-legend-icon" :src="legendIconUrl" />
                    </b-col>
                    <b-col cols="5" class="text-center">
                        <div class="raster-legend-text" v-if="lastLegendText">
                            {{ lastLegendText }}
                        </div>
                    </b-col>
                </b-row>
                <div class="mt-1 text-center raster-update-text" v-if="lastUpdateText">
                    Cập nhật lúc: {{ lastUpdateText }}
                </div>
            </b-card>
            <b-card class="detail-card mb-0" header-class="rainValueGather10-card-bg" header-text-variant="white">
                <div>
                    <apex-chart :type="chartType" :height="320" :options="chartOptions" :series="seriesData" />
                </div>
            </b-card>
        </template>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import API_CONFIG from '@/api-config';

export default {
    props: {
        pointDetail: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            loading: false,
            errorMessage: '',
            detail: {}
        };
    },
    watch: {
        pointDetail: {
            immediate: true,
            handler(value) {
                this.loadPointDetail(value);
            }
        }
    },
    computed: {
        detailData() {
            return this.detail || {};
        },
        nameText() {
            return this.detailData?.name;
        },
        addressWithCoordinates() {
            const address = this.detailData?.address || '';
            const lat = this.detailData?.lat;
            const lng = this.detailData?.lng;
            if (lat == null || lng == null) return address;
            return `${address} (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
        },
        values() {
            if (!Array.isArray(this.detailData?.values)) return [];
            return this.detailData.values;
        },
        lastValueText() {
            if (this.values.length === 0) return '';
            const lastItem = this.values[this.values.length - 1];
            const value = lastItem?.value;
            if (value == null || Number.isNaN(value)) return '0';
            return this.formatValue(value);
        },
        lastUpdateText() {
            return this.formatFullTime(this.latestItemBeforeNow?.time);
        },
        latestItemBeforeNow() {
            if (this.values.length === 0) return null;
            const now = moment();
            let nearestItem = null;

            for (const item of this.values) {
                const itemTime = moment(item?.time);
                if (!itemTime.isValid() || itemTime.isAfter(now)) continue;
                if (!nearestItem || itemTime.isAfter(moment(nearestItem.time))) {
                    nearestItem = item;
                }
            }

            return nearestItem;
        },
        lastValueNumber() {
            if (this.values.length === 0) return null;
            const lastItem = this.values[this.values.length - 1];
            const value = Number(lastItem?.value);
            if (Number.isNaN(value)) return null;
            return value;
        },
        legend() {
            if (!Array.isArray(this.detailData?.legend)) return [];
            return this.detailData.legend;
        },
        chartType() {
            return this.detailData?.chartType || 'line';
        },
        matchedLegendItem() {
            if (this.legend.length === 0 || this.lastValueNumber == null) return null;
            for (const item of this.legend) {
                if (this.lastValueNumber <= item.value) return item;
            }
            return this.legend[this.legend.length - 1] || null;
        },
        lastLegendText() {
            const item = this.matchedLegendItem;
            if (!item) return '';
            return item.name || '';
        },
        legendIconUrl() {
            const icon = this.matchedLegendItem?.icon;
            if (!icon) return '';
            if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
            return API_CONFIG.BASE_URL + 'download?fileName=' + icon;
        },
        themeColor() {
            return '#4a90d9';
        },
        headerThemeColor() {
            return this.darkenHexColor(this.themeColor, 24);
        },
        cardThemeStyle() {
            return {
                '--raster-card-bg': this.themeColor,
                '--raster-card-header-bg': this.headerThemeColor
            };
        },
        categories() {
            return this.values.map((item, index) => {
                const current = moment(item?.time);
                if (!current.isValid()) return '';
                if (index === 0) return current.format('DD/MM');

                const previous = moment(this.values[index - 1]?.time);
                if (!previous.isValid()) return current.format('HH:mm');
                if (!current.isSame(previous, 'day')) return current.format('DD/MM');
                return current.format('HH:mm');
            });
        },
        futureStartIndex() {
            const now = moment();
            return this.values.findIndex(item => {
                const itemTime = moment(item?.time);
                return itemTime.isValid() && !itemTime.isBefore(now);
            });
        },
        seriesData() {
            const rawData = this.values.map(item => item?.value ?? 0);
            if (!['line', 'bar'].includes(this.chartType)) {
                return [
                    {
                        name: 'Giá trị',
                        data: rawData
                    }
                ];
            }

            const now = moment();
            const pastData = [];
            const futureData = [];

            this.values.forEach((item, index) => {
                const itemTime = moment(item?.time);
                const value = rawData[index];
                const isFuture = itemTime.isValid() && !itemTime.isBefore(now);

                if (isFuture) {
                    pastData.push(null);
                    futureData.push(value);
                    return;
                }

                pastData.push(value);
                futureData.push(null);
            });

            if (this.chartType === 'line' && this.futureStartIndex > 0) {
                const bridgeIndex = this.futureStartIndex - 1;
                if (pastData[bridgeIndex] != null) {
                    futureData[bridgeIndex] = pastData[bridgeIndex];
                }
            }

            return [
                {
                    name: 'Giá trị',
                    data: pastData
                },
                {
                    name: 'Giá trị',
                    data: futureData
                }
            ];
        },
        chartOptions() {
            const isLineChart = this.chartType === 'line';
            const isBarChart = this.chartType === 'bar';
            return {
                chart: {
                    id: 'raster-point-detail',
                    stacked: isBarChart,
                    toolbar: {
                        tools: {
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            reset: false,
                            pan: false
                        }
                    }
                },
                stroke: {
                    curve: 'smooth',
                    width: isLineChart ? [2, 2] : 2,
                    dashArray: isLineChart ? [0, 6] : 0
                },
                colors: ['#2E93fA', '#E53935'],
                fill: isBarChart
                    ? {
                        type: ['solid', 'pattern'],
                        pattern: {
                            style: ['squares', 'slantedLines'],
                            width: 6,
                            height: 6,
                            strokeWidth: 2
                        }
                    }
                    : {
                        type: 'solid'
                    },
                legend: {
                    show: false
                },
                dataLabels: {
                    enabled: false
                },
                grid: {
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                title: {
                    text: 'Lịch sử',
                    align: 'center',
                },
                xaxis: {
                    categories: this.categories,
                    tickAmount: Math.min(6, this.categories.length || 0),
                    labels: {
                        rotate: 0,
                        hideOverlappingLabels: true
                    }
                },
                yaxis: {
                    labels: {
                        formatter: value => this.formatValue(value)
                    }
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (value, { dataPointIndex }) =>
                            this.formatFullTime(this.values[dataPointIndex]?.time)
                    },
                    y: {
                        title: {
                            formatter: () => ''
                        },
                        formatter: (value, { dataPointIndex }) =>
                            `${this.formatValue(value)}`
                    }
                }
            };
        }
    },
    methods: {
        ...mapActions({
            getRasterPointDetail: 'mapLayer/getPointDetail'
        }),
        async loadPointDetail(value) {
            if (!value?.id || value?.lat == null || value?.lng == null) {
                this.detail = {};
                this.loading = false;
                this.errorMessage = '';
                return;
            }

            this.loading = true;
            this.errorMessage = '';
            this.detail = {
                ...value,
                name: '',
                address: '',
                values: []
            };

            try {
                const pointDetail = await this.getRasterPointDetail({
                    id: value.id,
                    lat: value.lat,
                    lng: value.lng
                });
                this.detail = {
                    ...value,
                    name: pointDetail?.name,
                    address: pointDetail?.address,
                    values: pointDetail?.values || []
                };
            } catch (error) {
                this.errorMessage = 'Không thể tải thông tin điểm. Vui lòng thử lại.';
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        formatTime(value) {
            if (!value) return '';
            return moment(value).format('HH:mm DD/MM');
        },
        formatFullTime(value) {
            if (!value) return '';
            return moment(value).format('DD/MM/YYYY HH:mm');
        },
        formatValue(value) {
            return value?.toFixed(2) ?? '';
        },
        darkenHexColor(color, amount) {
            const hex = (color || '').replace('#', '');
            if (![3, 6, 8].includes(hex.length)) return color || '#4d9bcf';
            const rawHex =
                hex.length === 8
                    ? hex.substring(0, 6)
                    : hex;
            const fullHex =
                rawHex.length === 3
                    ? rawHex
                        .split('')
                        .map(char => char + char)
                        .join('')
                    : rawHex;
            const r = Math.max(0, parseInt(fullHex.substring(0, 2), 16) - amount);
            const g = Math.max(0, parseInt(fullHex.substring(2, 4), 16) - amount);
            const b = Math.max(0, parseInt(fullHex.substring(4, 6), 16) - amount);
            const toHex = value => value.toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        }
    }
};
</script>

<style scoped>
.raster-loading {
    min-height: 220px;
}

.raster-info-card {
    background-color: var(--raster-card-bg, #6fb8e4);
    color: #fff;
}

.raster-name-text {
    font-size: 1rem;
}

.raster-address-text {
    font-size: 0.875rem;
}

.raster-last-value-text {
    font-size: 3.4rem;
    line-height: 1.1;
}

.raster-body-row {
    min-height: 82px;
}

.raster-legend-icon {
    width: 68px;
    height: 68px;
    object-fit: contain;
}

.raster-legend-text {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
}

.raster-update-text {
    font-size: 0.8rem;
    opacity: 0.95;
}

::v-deep .raster-info-card .card-header {
    background-color: var(--raster-card-header-bg, #4d9bcf);
    color: #fff;
    border-bottom: 0;
    padding-bottom: 0.45rem;
}

::v-deep .raster-info-card .card-body {
    padding: 0.45rem 2rem 0.6rem;
}
</style>
